import type { MouseEventHandler, FC } from 'react';

import type { PopoverProps } from '@packages/shared';
import {
  Box,
  ClickAwayListener,
  Container,
  Fade,
  Popper,
  Stack,
  useDeviceType,
} from '@packages/shared';

import type { NavigationItem } from '../navigationTypes';
import type { ResponsiveFlyoutHeaderProps } from './ResponsiveFlyoutHeader';
import { ResponsiveFlyoutHeader } from './ResponsiveFlyoutHeader';
import { getNavigationItemKey } from '../getNavigationItemKey';
import { ResponsiveFlyoutTransition } from './ResponsiveFlyoutTransition';
import { ResponsiveFlyoutContent } from './ResponsiveFlyoutContent';
import type { TreeNavigationDirection } from './useTreeNavigationPopup';

export type ResponsiveFlyoutProps = {
  item?: NavigationItem;
  direction: TreeNavigationDirection;
  onItemClick?: (item: NavigationItem) => void;
  onNavigate?: (item: NavigationItem) => void;
  onMouseLeave?: MouseEventHandler<HTMLDivElement>;
} & Pick<ResponsiveFlyoutHeaderProps, 'onClose' | 'onBack'> &
  Pick<PopoverProps, 'open' | 'anchorEl'>;

/**
 * Navigation popup shown to the user when they interact with the main navigation, displaying child navigation items in the nav tree.
 */
export const ResponsiveFlyout: FC<ResponsiveFlyoutProps> = ({
  open,
  anchorEl,
  item,
  direction,
  onClose,
  onBack,
  onItemClick,
  onNavigate,
  onMouseLeave,
}) => {
  const { isDesktop } = useDeviceType();

  const transitionKey = item && getNavigationItemKey(item);

  return (
    <Popper
      anchorEl={isDesktop ? anchorEl : document.body}
      placement={isDesktop ? 'bottom' : 'top-start'}
      open={open}
      sx={{ width: '100%', zIndex: (theme) => theme.zIndex.drawer, lg: { height: '100%' } }}
      transition
      role="dialog"
      onMouseLeave={onMouseLeave}
      id="main-navigation-flyout"
    >
      {({ TransitionProps }) => (
        <Fade {...TransitionProps}>
          <Box>
            <ClickAwayListener onClickAway={() => onClose?.()}>
              <Box
                sx={{
                  width: '100%',
                  paddingBottom: 4,
                  backgroundColor: 'grey.light',
                }}
              >
                <Container>
                  <Stack gap={2}>
                    <ResponsiveFlyoutHeader
                      title={item?.displayName}
                      onBack={onBack}
                      onClose={onClose}
                    />
                    <ResponsiveFlyoutTransition direction={direction} transitionKey={transitionKey}>
                      <ResponsiveFlyoutContent
                        item={item}
                        onItemClick={onItemClick}
                        onNavigate={onNavigate}
                      />
                    </ResponsiveFlyoutTransition>
                  </Stack>
                </Container>
              </Box>
            </ClickAwayListener>
          </Box>
        </Fade>
      )}
    </Popper>
  );
};

// eslint-disable-next-line import/no-default-export
export default ResponsiveFlyout;
